<script setup lang="ts">
/**
 * Бейдж пайплайна — макет 5:74: точка статуса, номер прогона и длительность.
 *
 * Подложка только у бегущего и упавшего: это два состояния, ради которых на
 * бейдж и смотрят, а зелёная пилюля у каждой строки превратила бы блок в
 * светофор.
 *
 * Ведёт в GitLab — там и стадии, и логи. Показать стадии здесь пока нечем:
 * зеркало вебхуков держит пайплайн целиком, без разбивки по джобам
 * (docs/design-gaps.md).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatDuration } from '../devinfo'

import type { DevPipeline } from '../devinfo'

const props = defineProps<{ pipeline: DevPipeline }>()

const { t, te } = useI18n()

/** Цвет точки: зелёный — прошёл, красный — упал, синий — идёт, серый — прочее */
const DOT_COLORS: Record<string, string> = {
  success: 'var(--color-success)',
  failed: 'var(--color-danger)',
  running: 'var(--color-info)',
  pending: 'var(--color-warning)',
  created: 'var(--color-warning)',
}

const TINTED = ['running', 'failed']

const dot = computed(() => DOT_COLORS[props.pipeline.status] ?? 'var(--color-text-muted)')

const duration = computed(() => formatDuration(props.pipeline.durationSeconds))

const label = computed(() => {
  const key = `issue.dev.pipeline.${props.pipeline.status}`

  return te(key) ? t(key) : props.pipeline.status
})
</script>

<template>
  <a
    class="pbadge"
    :class="{ 'pbadge--tinted': TINTED.includes(pipeline.status) }"
    :style="{ '--pbadge-dot': dot }"
    :href="pipeline.url"
    target="_blank"
    rel="noopener"
    :title="label"
  >
    <span class="pbadge__dot" aria-hidden="true" />
    <span class="pbadge__text">
      #{{ pipeline.id }}<template v-if="duration"> · {{ duration }}</template>
    </span>
  </a>
</template>

<style scoped>
.pbadge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: var(--radius-full);
  color: var(--color-text-secondary);
  text-decoration: none;
}

.pbadge--tinted {
  background: color-mix(in srgb, var(--pbadge-dot) 12%, transparent);
}

.pbadge:hover .pbadge__text {
  text-decoration: underline;
}

.pbadge__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: var(--pbadge-dot);
}

.pbadge__text {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  white-space: nowrap;
}
</style>
